import styles from "./styles.module.scss";

import classNames from "classnames";
import { FC } from "react";
import "../modal.scss";

import Arrow from "@assets/icons/Arrow";
import Dots from "@assets/icons/Dots";
import Setting from "@assets/icons/setting";
import WidgetIcon from "@assets/icons/widgetsDNDBtn";
import { MetricCard } from "@pages/CROModule/components/CROPage/components/Sections/MetricBuilder/MetricCard";
import { Widget } from "@pages/Dashboard/features/Widgets/Widget";
import { IWidget } from "@pages/Dashboard/types/dashboard.types";
import { WidgetMetricKey } from "@services/math/math.service";
import { SettingsModal } from "../SettingsSmallModal";
import { DragHandlers, GroupType } from "./types";

interface GroupAccordionProps {
  groupIndex: number;
  group: GroupType;
  isCRO?: boolean;
  opened: boolean;
  onToggle: () => void;
  openModalGroup: string | null;
  onOpenModal: (g: string | null) => void;
  widgetOrder: Record<string, WidgetMetricKey[]>;
  widgets: IWidget[];
  isWhatIf: boolean;
  historicalShowGroup: string[];
  onHistoricalToggle: (groupTitle: string, show: boolean) => void;
  choosedWidgets: WidgetMetricKey[];
  setChoosedWidgets: React.Dispatch<React.SetStateAction<WidgetMetricKey[]>>;
  resetToDefault: (groupTitle: string) => void;
  onApply: () => void;
  handlers: DragHandlers;
  groupHandlers: DragHandlers;
  itemRefsMap: React.MutableRefObject<Record<string, (HTMLElement | null)[]>>;
  groupRef: React.MutableRefObject<HTMLElement>;
}

export const GroupAccordion: FC<GroupAccordionProps> = ({
  groupIndex,
  group,
  isCRO,
  opened,
  onToggle,
  openModalGroup,
  onOpenModal,
  widgetOrder,
  widgets,
  isWhatIf,
  historicalShowGroup,
  onHistoricalToggle,
  choosedWidgets,
  setChoosedWidgets,
  resetToDefault,
  onApply,
  handlers,
  itemRefsMap,
  groupHandlers,
  groupRef,
}) => {
  if (group.title === "Brands") return null;

  const groupWidgets = widgetOrder[group.title] ?? group.keys;
  const isModalOpen = openModalGroup === group.title;
  return (
    <div
      key={group.title}
      className={classNames(styles.accordion, opened && styles.open, {
        [styles.isTarget]: groupHandlers.targetGroop == group.title,
      })}
    >
      <button
        ref={(el) => (groupRef.current = el)}
        className={classNames(styles.accordion__title)}
        type="button"
        onClick={onToggle}
        draggable
        onDragStart={(e) =>
          groupHandlers.handleDragStart(e, group.title, groupIndex)
        }
        onDrag={groupHandlers.handleDrag}
        onDragOver={(e) =>
          groupHandlers.handleDragOver(e, group.title, groupIndex)
        }
        onDragEnter={(e) =>
          groupHandlers.handleDragEnter(e, group.title, groupIndex)
        }
        onDragLeave={(e) => groupHandlers.handleDragLeave(e, groupIndex)}
        onDragEnd={groupHandlers.handleDragEnd}
        onTouchStart={(e) => {
          const touch = e.touches[0];
          if (!touch) return;
          groupHandlers.handleDragStart(
            {
              ...e,
              clientX: touch.clientX,
              clientY: touch.clientY,
              dataTransfer: {
                effectAllowed: "move",
                setDragImage: () => {},
              },
            } as unknown as React.DragEvent,
            group.title,
            groupIndex,
          );
        }}
        onTouchMove={(e) => groupHandlers.handleDrag(e as any)}
        onTouchEnd={groupHandlers.handleDragEnd}
      >
        <div className={styles.name}>
          <Arrow />
          {group.title}
        </div>
        <div className={styles.accordion__actions}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenModal(group.title);
            }}
          >
            <Setting />
          </button>
          <button className={styles.dragAction}>
            <Dots />
          </button>
        </div>
      </button>

      <div
        className={classNames(styles.accordion__content, {
          [styles.cro]: isCRO,
        })}
      >
        {groupWidgets.map((metric, index) => {
          const widget = widgets.find((w) => w.id === metric);
          if (!widget) return null;

          if (isWhatIf && group.title !== "Market") {
            return <MetricCard key={metric} metric={widget} />;
          }

          return (
            <button
              key={metric}
              type="button"
              ref={(el) => {
                if (!itemRefsMap.current[group.title]) {
                  itemRefsMap.current[group.title] = [];
                }
                itemRefsMap.current[group.title][index] = el;
              }}
              className={classNames(styles.widgetWrapper, {
                [styles.isTarget]:
                  handlers.selectedGroup === group.title &&
                  handlers.targetIndex === index,
              })}
              draggable
              onDragStart={(e) =>
                handlers.handleDragStart(e, group.title, index)
              }
              onDrag={handlers.handleDrag}
              onDragOver={(e) => handlers.handleDragOver(e, group.title, index)}
              onDragEnter={(e) =>
                handlers.handleDragEnter(e, group.title, index)
              }
              onDragLeave={(e) => handlers.handleDragLeave(e, index)}
              onDragEnd={handlers.handleDragEnd}
              onTouchStart={(e) => {
                const touch = e.touches[0];
                if (!touch) return;
                handlers.handleDragStart(
                  {
                    ...e,
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                    dataTransfer: {
                      effectAllowed: "move",
                      setDragImage: () => {},
                    },
                  } as unknown as React.DragEvent,
                  group.title,
                  index,
                );
              }}
              onTouchMove={(e) => handlers.handleDrag(e as any)}
              onTouchEnd={() => handlers.handleDragEnd()}
            >
              <div className={styles.dragHandle}>
                {typeof WidgetIcon === "function" ? <WidgetIcon /> : "≡"}
              </div>
              <Widget
                widget={widget}
                additionalClass={classNames(styles.widget, {
                  [styles.historical]: historicalShowGroup.includes(
                    group.title,
                  ),
                })}
              />
            </button>
          );
        })}
      </div>

      <SettingsModal
        open={isModalOpen}
        title={group.title}
        availableKeys={group.keys}
        selectedKeys={choosedWidgets}
        onClose={() => onOpenModal(null)}
        onChange={(keys) => setChoosedWidgets(keys)}
        resetToDefault={() => resetToDefault(group.title)}
        onApply={onApply}
        setShowHistorical={(show) => onHistoricalToggle(group.title, show)}
        showHistorical={historicalShowGroup.includes(group.title)}
      />
    </div>
  );
};
